/**
 * Grammar overview page component.
 *
 * Displays all available grammar topics organized by proficiency level (A1-C2).
 * Provides search functionality, level filtering, bookmarking, and navigation
 * to individual grammar topic detail pages.
 *
 * Route: `/learning/grammar`
 * Requires authentication: Yes
 *
 * @module GrammarOverview
 */

import { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  ChevronUp,
  Bookmark,
  BookmarkCheck,
  Play,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SearchBar } from "@/components/learning/SearchBar";
import { grammarLevels } from "@/data/learningData";
import { ProficiencyLevel, GrammarTopic } from "@/types/learning";
import { loadGrammarBookmarks, saveGrammarBookmarks } from "@/lib/storage";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { LearningSectionSwitcher } from "@/components/learning/LearningSectionSwitcher";

export default function GrammarOverview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedLevels, setSelectedLevels] = useState<ProficiencyLevel[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedLevels, setExpandedLevels] = useState<Set<ProficiencyLevel>>(
    new Set(["A1"]),
  );
  const [bookmarkedTopics, setBookmarkedTopics] = useState<Set<string>>(
    loadGrammarBookmarks(),
  );
  const lockedLevels = useMemo(
    () => new Set<ProficiencyLevel>(["A2", "B1", "B2", "C1", "C2"]),
    [],
  );

  const isLevelLocked = (level: ProficiencyLevel) => {
    return lockedLevels.has(level);
  };

  // Update bookmarks in localStorage when they change
  useEffect(() => {
    saveGrammarBookmarks(bookmarkedTopics);
  }, [bookmarkedTopics]);

  const filteredAndSortedTopics = useMemo(() => {
    let topics = grammarLevels.flatMap((level) =>
      level.topics.map((topic) => ({ ...topic, level: level.level })),
    );

    // Filter by selected levels
    if (selectedLevels.length > 0) {
      topics = topics.filter((topic) => selectedLevels.includes(topic.level));
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      topics = topics.filter(
        (topic) =>
          topic.title.toLowerCase().includes(query) ||
          topic.description.toLowerCase().includes(query),
      );
    }

    return topics;
  }, [selectedLevels, searchQuery]);

  const handleLevelToggle = (level: ProficiencyLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  };

  const handleClearLevels = () => {
    setSelectedLevels([]);
  };

  const toggleLevelExpanded = (level: ProficiencyLevel) => {
    if (isLevelLocked(level)) return;
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const handleTopicClick = (level: ProficiencyLevel, topicId: string) => {
    navigate(`/learning/grammar/${level}/${topicId}`);
  };

  const handleBookmarkToggle = (topicId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setBookmarkedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) {
        next.delete(topicId);
      } else {
        next.add(topicId);
      }
      return next;
    });
  };

  const getActionButton = (topic: GrammarTopic, level: ProficiencyLevel) => {
    const status = topic.status || "not-started";

    const handleButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleTopicClick(level, topic.id);
    };

    if (status === "completed") {
      return (
        <Button size="sm" variant="outline" onClick={handleButtonClick}>
          {t("common.review", { defaultValue: "Review" })}
        </Button>
      );
    } else if (status === "in-progress") {
      return (
        <Button size="sm" onClick={handleButtonClick}>
          <Play className="h-3 w-3 mr-1.5" />
          {t("common.continue", { defaultValue: "Continue" })}
        </Button>
      );
    } else {
      return (
        <Button size="sm" onClick={handleButtonClick}>
          <Play className="h-3 w-3 mr-1.5" />
          {t("common.start", { defaultValue: "Start" })}
        </Button>
      );
    }
  };

  return (
    <StandardPageShell
      title={t("learning.grammar.title", { defaultValue: "Grammar" })}
      headerRight={<LanguageSwitcher />}
      className="mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8"
    >
      <div>
        <LearningSectionSwitcher active="grammar" />
        <div className="mb-6">
          <p className="font-ui mb-6 text-sm text-muted-foreground">
            {t("learning.grammar.subtitle", {
              defaultValue:
                "Learn German grammar topics organized by proficiency level",
            })}
          </p>

          <div className="mb-4 flex items-center gap-4">
            <SearchBar
              placeholder={t("learning.searchPlaceholder", {
                defaultValue: "Search grammar topics…",
              })}
              onSearch={setSearchQuery}
              className="max-w-none w-full"
            />
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-ui text-sm font-medium text-foreground">
                {t("learning.sidebar.level", { defaultValue: "Level" })}
              </p>
              {selectedLevels.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearLevels}>
                  {t("learning.clearFilters", {
                    defaultValue: "Clear filters",
                  })}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {(["A1", "A2", "B1", "B2", "C1", "C2"] as ProficiencyLevel[]).map(
                (level) => {
                  const selected = selectedLevels.includes(level);
                  const locked = isLevelLocked(level);
                  return (
                    <Button
                      key={level}
                      type="button"
                      variant={selected ? "default" : "outline"}
                      size="sm"
                      aria-pressed={selected}
                      aria-disabled={locked}
                      onClick={() => !locked && handleLevelToggle(level)}
                      className={cn("gap-2", locked && "cursor-not-allowed")}
                    >
                      <span>{level}</span>
                      {locked && <Lock className="h-3 w-3" />}
                    </Button>
                  );
                },
              )}
            </div>
          </div>
        </div>

        {/* Level Accordions */}
        {grammarLevels.map((level) => {
          const isExpanded = expandedLevels.has(level.level);
          const levelTopics = filteredAndSortedTopics.filter(
            (t) => t.level === level.level,
          );
          const completedCount = levelTopics.filter(
            (t) => t.status === "completed",
          ).length;

          if (
            levelTopics.length === 0 &&
            selectedLevels.length > 0 &&
            !selectedLevels.includes(level.level)
          ) {
            return null;
          }

          const isLocked = isLevelLocked(level.level);

          return (
            <div
              key={level.level}
              className={cn("mb-4", isLocked && "opacity-75")}
            >
              <button
                onClick={() => toggleLevelExpanded(level.level)}
                className={cn(
                  "w-full flex items-center justify-between p-4 bg-card border-2 border-border rounded-xl transition-colors",
                  isLocked
                    ? "cursor-default grayscale-[0.5]"
                    : "hover:border-primary/50 cursor-pointer",
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "px-3 py-1 rounded-full font-ui font-semibold text-sm",
                      isLocked
                        ? "bg-muted text-muted-foreground"
                        : "bg-primary/10 text-primary",
                    )}
                  >
                    {level.level}
                  </span>
                  <div className="text-left">
                    <div className="flex items-center gap-2">
                      <h3 className="font-ui font-semibold text-lg text-foreground">
                        {level.name}
                      </h3>
                      {isLocked && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-500 border border-amber-500/20">
                          <Lock className="h-3 w-3" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">
                            {t("common.comingSoon", {
                              defaultValue: "Coming Soon",
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="font-ui text-sm text-muted-foreground">
                      {t("learning.topics", {
                        count: levelTopics.length,
                        defaultValue: "topics",
                      })}
                      {!isLocked &&
                        completedCount > 0 &&
                        ` • ${completedCount}/${levelTopics.length} ${t("common.completed", { defaultValue: "completed" })}`}
                    </p>
                  </div>
                </div>
                {!isLocked &&
                  (isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-muted-foreground" />
                  ))}
              </button>

              {isExpanded && (
                <div className="mt-2 space-y-2">
                  {levelTopics.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <p className="font-ui">
                        {t("learning.noTopics", {
                          defaultValue: "No topics found",
                        })}
                      </p>
                      {(selectedLevels.length > 0 || searchQuery) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => {
                            setSelectedLevels([]);
                            setSearchQuery("");
                          }}
                        >
                          {t("learning.clearFilters", {
                            defaultValue: "Clear filters",
                          })}
                        </Button>
                      )}
                    </div>
                  ) : (
                    levelTopics.map((topic) => (
                      <div
                        key={topic.id}
                        className="w-full flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-card border border-border rounded-lg hover:border-primary/50 transition-colors gap-4"
                      >
                        <button
                          type="button"
                          onClick={() =>
                            handleTopicClick(level.level, topic.id)
                          }
                          className="flex-1 min-w-0 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                        >
                          <h4 className="font-ui font-semibold text-base text-foreground mb-1">
                            {topic.title}
                          </h4>
                          <p className="font-ui text-sm text-muted-foreground line-clamp-2 sm:truncate">
                            {topic.description}
                          </p>
                        </button>
                        <div className="flex items-center justify-between sm:justify-end gap-3 w-full sm:w-auto mt-2 sm:mt-0 pt-3 sm:pt-0 border-t sm:border-t-0 border-border">
                          {getActionButton(topic, level.level)}
                          <button
                            onClick={(e) => handleBookmarkToggle(topic.id, e)}
                            className="p-2 hover:bg-accent rounded-lg transition-colors"
                            aria-label="Bookmark"
                          >
                            {bookmarkedTopics.has(topic.id) ? (
                              <BookmarkCheck className="h-4 w-4 text-primary" />
                            ) : (
                              <Bookmark className="h-4 w-4 text-muted-foreground" />
                            )}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </StandardPageShell>
  );
}
